<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showDeletePopup = ref(false);
const addAgentRef = ref(null);
const editAgentRef = ref(null);
const currentAgent = ref({});
const searchQuery = ref('');

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentList.value;
  return picoSearch(agentList.value, query, ['name', 'email']);
});

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const isAdminLikeRole = role => ['owner', 'administrator'].includes(role);

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => isAdminLikeRole(agent.role) && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (isAdminLikeRole(agent.role)) {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  useAlert(message);
};

const openAddPopup = () => {
  addAgentRef.value?.open();
};

const openEditPopup = agent => {
  editAgentRef.value?.open(agent);
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};

const tryOpenRevoke = agent => {
  if (!showDeleteAction(agent)) return;
  openDeletePopup(agent);
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="agents"
      >
        <template v-if="agentList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.COUNT', { n: agentList.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredAgentList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('AGENT_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="flex flex-col gap-3 pt-4">
        <div
          v-for="agent in filteredAgentList"
          :key="agent.id"
          class="flex flex-row items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-5 py-3"
        >
          <div class="flex min-w-0 flex-1 items-center gap-4">
            <Avatar
              :src="agent.thumbnail"
              :name="agent.name"
              :status="agent.availability_status"
              :size="48"
              rounded-full
            />
            <div class="min-w-0 flex-1">
              <p
                class="mb-0 truncate text-base font-semibold leading-snug text-n-slate-12"
              >
                <span>{{ agent.name }}</span>
                <span class="ml-2 font-normal text-sm text-n-slate-11">
                  {{ $t('AGENT_MGMT.LIST.ID_SUFFIX', { id: agent.id }) }}
                </span>
              </p>
              <p class="mb-0 truncate text-sm text-n-slate-11">
                {{ getAgentRoleName(agent)
                }}{{ $t('AGENT_MGMT.LIST.ROLE_EMAIL_SEPARATOR')
                }}{{ agent.email }}
              </p>
            </div>
          </div>

          <span
            class="hidden max-w-[7rem] shrink-0 truncate text-right text-sm text-n-slate-10 md:inline-block"
          >
            <template v-if="agent.confirmed">
              {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
            </template>
            <template v-else>
              {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
            </template>
          </span>

          <div
            class="flex shrink-0 flex-wrap items-center justify-end gap-3 sm:gap-4"
          >
            <Button
              v-if="currentUserId !== agent.id"
              variant="link"
              sm
              icon="i-lucide-circle-slash"
              :label="$t('AGENT_MGMT.REVOKE.BUTTON_TEXT')"
              class="!px-1 !font-bold"
              :is-loading="loading[agent.id]"
              :disabled="!showDeleteAction(agent)"
              v-bind="showDeleteAction(agent) ? { ruby: '' } : { slate: '' }"
              @click="tryOpenRevoke(agent)"
            />
            <Button
              v-if="showEditAction(agent)"
              outline
              slate
              sm
              icon="i-lucide-square-pen"
              :label="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
              class="rounded-xl !font-bold"
              @click="openEditPopup(agent)"
            />
          </div>
        </div>
      </div>
    </template>

    <AddAgent ref="addAgentRef" />

    <EditAgent ref="editAgentRef" />

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
